/**
 * Analytics Portal V1 — shared audience breakdown (Phase 9, plan §10.8 + §12.2).
 *
 * Renders `audience = {dimension: {available, partial, reason, notice, rows[]}}` exactly as the
 * server suppressed it. Rows already survived the minimum-audience, small-segment, `Other`
 * merge and lone-segment rules with percentages recomputed post-merge, so this component adds
 * no arithmetic of its own and can never surface a suppressed cell.
 */
import React, { useState } from "react";
import { ChartCard } from "./ChartCard";
import { AvailabilityNotice } from "./AvailabilityNotice";

export const DIMENSION_LABEL = {
  identity_type: "Visitor type",
  new_vs_returning: "New vs returning",
  industry: "Industry",
  classification: "Classification",
  country: "Country",
  state: "Region",
};

export const AudienceBreakdown = ({ audience, title = "Audience",
                                    labels = DIMENSION_LABEL, testId = "analytics-audience",
                                    description = "Server-suppressed groupings — small segments are merged and never itemised." }) => {
  const dimensions = Object.keys(audience || {}).filter((dim) => labels[dim]);
  const [active, setActive] = useState(dimensions[0] || null);
  if (!dimensions.length) return null;
  const current = audience[active] || {};
  const rows = current.rows || [];

  return (
    <ChartCard title={title} description={description} testId={testId}
               summary={`${labels[active]}: ${rows.map((r) => `${r.label} ${r.percent}%`).join("; ") || "no segments available"}`}>
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Audience dimension">
        {dimensions.map((dim) => (
          <button
            key={dim} type="button" role="tab" aria-selected={dim === active}
            onClick={() => setActive(dim)}
            className={`rounded-full border px-3 py-1 text-xs font-[560] transition-colors ${
              dim === active
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
            data-testid={`analytics-audience-tab-${dim}`}
          >
            {labels[dim]}
          </button>
        ))}
      </div>

      <div className="mt-4" data-testid={`analytics-audience-${active}`}>
        {!current.available ? (
          <AvailabilityNotice notice={current.notice} reason={current.reason}
                              testId={`analytics-audience-suppressed-${active}`} />
        ) : (
          <ul className="space-y-2.5">
            {rows.map((row) => (
              <li key={row.value} data-testid={`analytics-audience-row-${active}-${row.value}`}>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-foreground">{row.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {row.percent}% · {row.unique_visitors?.toLocaleString?.() ?? row.unique_visitors}
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-accent">
                  <div className="h-full rounded-full bg-primary/80"
                       style={{ width: `${Math.max(2, row.percent || 0)}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </ChartCard>
  );
};
